'use client'

import { useTranslations } from 'next-intl'
import { useEffect, useLayoutEffect, useRef, useState } from 'react'
import { createPortal } from 'react-dom'

interface OptionRowContextMenuProps {
  /** Viewport coordinates of the right-click. */
  x: number
  y: number
  onClose: () => void
  onOpenInChart: () => void
}

const MENU_WIDTH = 176
const ITEM_CLASS =
  'text-popover-foreground/80 hover:bg-foreground/10 hover:text-popover-foreground flex w-full cursor-default items-center px-3 py-2 text-left text-sm transition-colors'

/**
 * Self-managed right-click menu for an option chain row. Mirrors
 * WatchlistContextMenu / PositionContextMenu / OrderRowContextMenu: opens
 * instantly on right-click, stays open until an item is chosen or the user
 * clicks/scrolls away or presses Escape.
 */
export function OptionRowContextMenu({ x, y, onClose, onOpenInChart }: OptionRowContextMenuProps) {
  const t = useTranslations('trade')
  const menuRef = useRef<HTMLDivElement>(null)
  const [pos, setPos] = useState({ left: x, top: y })

  const onCloseRef = useRef(onClose)
  onCloseRef.current = onClose

  useLayoutEffect(() => {
    const el = menuRef.current
    if (!el) return
    const { width, height } = el.getBoundingClientRect()
    const margin = 8
    let left = x
    let top = y
    if (left + width > window.innerWidth - margin) {
      left = Math.max(margin, window.innerWidth - width - margin)
    }
    if (top + height > window.innerHeight - margin) {
      top = Math.max(margin, window.innerHeight - height - margin)
    }
    setPos({ left, top })
  }, [x, y])

  useEffect(() => {
    const close = () => onCloseRef.current()
    const onDocMouseDown = (e: MouseEvent) => {
      // A right-button press is a row opening a fresh menu — let it reposition
      // instead of closing-then-reopening.
      if (e.button === 2) return
      if (menuRef.current && !menuRef.current.contains(e.target as Node)) close()
    }
    const onKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape') close()
    }
    document.addEventListener('mousedown', onDocMouseDown, true)
    document.addEventListener('keydown', onKeyDown, true)
    // Capture phase: scroll doesn't bubble, and the menu must also close when
    // scrolling happens inside the option table's own scroll container.
    window.addEventListener('scroll', close, true)
    window.addEventListener('resize', close)
    window.addEventListener('blur', close)
    return () => {
      document.removeEventListener('mousedown', onDocMouseDown, true)
      document.removeEventListener('keydown', onKeyDown, true)
      window.removeEventListener('scroll', close, true)
      window.removeEventListener('resize', close)
      window.removeEventListener('blur', close)
    }
  }, [])

  const run = (fn: () => void) => () => {
    fn()
    onClose()
  }

  if (typeof window === 'undefined') return null

  return createPortal(
    <div
      ref={menuRef}
      role="menu"
      style={{ position: 'fixed', left: pos.left, top: pos.top, width: MENU_WIDTH, zIndex: 60 }}
      className="menu-surface"
      onContextMenu={(e) => e.preventDefault()}
    >
      <button type="button" className={ITEM_CLASS} onClick={run(onOpenInChart)}>
        {t('optionRowOpenInChart')}
      </button>
    </div>,
    document.body
  )
}
